import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, MessageCircle, Send, Loader2 } from "@/components/icons";
import api, { errMsg } from "@/lib/api";
import { Loading, ErrorState } from "@/components/common";
import Voltar from "@/components/Voltar";
import { useAuth } from "@/context/AuthContext";
import { fmtDateTime } from "@/lib/format";

const fmtPrazo = (iso) => {
  if (!iso) return "";
  const [y, m, d] = String(iso).split("-");
  return y && m && d ? `${d}/${m}/${y}` : iso;
};

function Bubble({ author, when, text, mine, badge }) {
  return (
    <div className={`flex ${mine ? "justify-end" : "justify-start"}`}>
      <div className={`max-w-[85%] rounded-2xl px-4 py-2.5 shadow-sm ${mine ? "bg-brand text-white rounded-tr-sm" : "bg-slate-100 text-navy rounded-tl-sm"}`}>
        <div className={`text-[11px] font-semibold flex items-center gap-1.5 ${mine ? "text-white/90" : "text-slate-500"}`}>
          <span>{author}</span>
          {badge && (
            <span className={`uppercase tracking-widest text-[9px] font-bold rounded px-1.5 py-0.5 ${mine ? "bg-white/20" : "bg-slate-300/70 text-slate-700"}`}>{badge}</span>
          )}
        </div>
        <div className="whitespace-pre-wrap text-sm leading-snug mt-0.5">{text}</div>
        <div className={`text-[10px] mt-1 ${mine ? "text-white/80" : "text-slate-400"}`}>{fmtDateTime(when)}</div>
      </div>
    </div>
  );
}

export default function Solicitacao() {
  const nav = useNavigate();
  const { id } = useParams();
  const { user } = useAuth();
  const [doc, setDoc] = useState(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState(null);
  const [resposta, setResposta] = useState("");
  const [enviando, setEnviando] = useState(false);
  const scrollRef = useRef(null);

  const load = async () => {
    setCarregando(true); setErro(null);
    try {
      const { data } = await api.get(`/cobrancas-whatsapp/${id}`);
      setDoc(data);
    } catch (e) {
      setErro(errMsg(e));
    } finally { setCarregando(false); }
  };
  useEffect(() => { load(); }, [id]);

  // Rola para a última mensagem sempre que a thread muda.
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [doc?.respostas?.length]);

  const responder = async () => {
    const text = resposta.trim();
    if (!text) { toast.error("Escreva a resposta"); return; }
    setEnviando(true);
    try {
      await api.post(`/cobrancas-whatsapp/${id}/responder`, { text });
      setResposta("");
      await load();
      toast.success("Resposta registrada");
    } catch (e) { toast.error(errMsg(e)); }
    finally { setEnviando(false); }
  };

  if (carregando) return <Loading />;
  if (erro) return <ErrorState onRetry={load} message={erro} />;
  if (!doc) return null;

  const sou_gestor = user?.id === doc.manager_id;
  const sou_vendedor = user?.id === doc.vendedor_id;
  const posso_responder = sou_gestor || sou_vendedor || user?.role === "admin";
  const voltar = () => doc.cliente_id ? nav(`/cliente/${doc.cliente_id}`) : nav(-1);

  return (
    <div className="max-w-3xl mx-auto pb-24 md:pb-6" data-testid="solicitacao-page">
      <Voltar fallback={doc.cliente_id ? `/cliente/${doc.cliente_id}` : "/clientes"} className="mb-4" />

      <div className="bg-card rounded-2xl border border-black/5 shadow-[0_2px_10px_rgb(0,0,0,0.03)] overflow-hidden">
        {/* Cabeçalho */}
        <header className="px-5 py-4 bg-green-50 border-b border-green-600/20 flex items-start gap-3">
          <MessageCircle className="w-5 h-5 text-green-700 mt-0.5 shrink-0" />
          <div className="flex-1 min-w-0">
            <h1 className="text-base font-bold text-navy leading-tight">
              Solicitação de {doc.manager_name} <span className="text-slate-500 font-medium">para</span> {doc.vendedor_name}
            </h1>
            <div className="text-xs text-slate-600 mt-1 flex flex-wrap gap-x-3 gap-y-0.5">
              {doc.cliente_id && doc.cliente_nome && (
                <button onClick={() => nav(`/cliente/${doc.cliente_id}`)} className="text-brand hover:underline font-medium" data-testid="solic-cliente-link">
                  Cliente: {doc.cliente_nome}
                </button>
              )}
              {doc.prazo && <span>Prazo: <strong>{fmtPrazo(doc.prazo)}</strong></span>}
              <span>Enviada em {fmtDateTime(doc.created_at)}</span>
              <span className="uppercase tracking-widest text-[10px] font-bold text-green-700">Enviada pelo WhatsApp</span>
            </div>
          </div>
        </header>

        {/* Thread */}
        <div ref={scrollRef} className="px-5 py-4 space-y-3 max-h-[62vh] overflow-y-auto" data-testid="solic-thread">
          <Bubble
            author={doc.manager_name}
            when={doc.created_at}
            text={doc.text}
            mine={user?.id === doc.manager_id}
            badge="Pedido"
          />
          {(doc.respostas || []).map((r) => (
            <Bubble
              key={r.id}
              author={r.author_name}
              when={r.created_at}
              text={r.text}
              mine={user?.id === r.author_id}
              badge={r.author_role === "manager" ? "Gestor" : "Vendedor"}
            />
          ))}
        </div>

        {/* Caixa de resposta */}
        {posso_responder ? (
          <div className="border-t border-black/5 p-3 flex items-end gap-2 bg-slate-50/50">
            <textarea
              value={resposta}
              onChange={(e) => setResposta(e.target.value)}
              rows={2}
              placeholder="Escreva sua resposta..."
              data-testid="solic-resposta"
              className="flex-1 px-3 py-2 rounded-lg border border-input bg-white text-sm resize-none focus:ring-2 focus:ring-brand/40 focus:outline-none"
            />
            <button onClick={responder} disabled={enviando || !resposta.trim()} data-testid="solic-enviar"
              className="h-10 px-4 rounded-lg bg-brand text-white text-sm font-semibold hover:bg-brand-hover disabled:opacity-50 flex items-center gap-1.5 shrink-0">
              {enviando ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Responder
            </button>
          </div>
        ) : (
          <div className="border-t border-black/5 p-3 text-center text-xs text-slate-500">
            Você não é parte desta solicitação.
          </div>
        )}
      </div>
    </div>
  );
}
